/**
 * 작성자 : 홍선영
 * 날짜 : 2024.01.17
 * 경로 : 설정관리 > 알림톡 담당자 관리
 */

import { useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { userState } from '../../../atoms';
import { IModal } from 'customTypes';
import { COMCD_USE_YN, ERROR, FLAG_CREATE_OR_UPDATE, FLAG_NONE, INIT_USE_YN_A } from '../../../_constants';
import i18n from '../../../translation/i18n';
import { InputTable } from '../../../assets/styles/InputTable';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import { BtnBlue, BtnGhost, BtnRed } from '../../../components/Button';
import Input from '../../../components/Input';
import Portal from '../../../components/Portal';
import SelectBoxs from '../../../components/SelectBoxs';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import SelectBox from '../../../components/SelectBox';
import { ynFilter } from '../../../utils/ynFilter';
import { useSetAuth } from '../../../utils/useSetAuth';
import { applyBorderStyle } from '../../../utils/applyBorderStyle';
import { scrollToNodeBottom } from '../../../utils/scrollToNodeBottom';
import { useQuery } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost } from '../../../services/_common';
import { toast } from 'react-toastify';
import { trimArray } from '../../../utils/trimArray';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import { PulseLoader } from 'react-spinners';
import IssueGuide from '../../../components/IssueGuide';
import { useFetchCommonCodeList } from '../../../services/useSetCodeListInSelectBoxForm';
import { logPost } from '../../../services/log';
import { useOutletContext } from 'react-router-dom';

const Root = styled(ContentsContainerRoot)`
  .inputFormsWrapper.smallTab {
    flex-direction: row;
    align-items: center;
    padding: 0 0.5rem;
    font-weight: 500;
    &::-webkit-scrollbar {
      -webkit-appearance: none;
      height: 0rem !important;
    }

    .tab {
      color: var(--text-secondary);
      display: flex;
      align-items: center;
      padding: 0 0.5rem;
      cursor: pointer;
      height: 3.5rem;
      border-bottom: 2px solid transparent;
    }
    .activeTab {
      font-weight: 700;
      border-bottom: 2px solid var(--selected-primary);
      color: var(--selected-primary);
    }
  }

  .tr > div > div {
    width: 100%;
    li {
      width: 100% !important;
    }
  }

  .secondSearchOption {
    width: fit-content;
  }

  .textBtnGroup button {
    padding: 0 0.75rem;
    width: 4.5rem;
    height: 2.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    border: 1px solid var(--outline);
    background-color: var(--tonal-light);
  }
`;

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.5rem;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;

interface ItuList {
  bigo: string;
  tuCd: string;
  tuName: string;
  tuTel: string;
  useYn?: string;
  writer?: string;
  wDate?: string;
  editor: string;
  eDate?: string;
  tuGubun?: string;
  flag?: string;
}

interface IData {
  tuGubun: string;
}

interface Props {
  prop: IData;
}

const gubunList = [
  { type: 'tuGubun', state: '07', cdName: '화재경보' },
  { type: 'tuGubun', state: '08', cdName: '침입경보' },
  { type: 'tuGubun', state: '09', cdName: '쓰러짐경보' },
];

const EmergencyList = ({ prop }: Props) => {
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const size = useOutletContext<any>();
  const userInfo = useRecoilValue(userState);
  const [tuGubun, setTuGubun] = useState(prop.tuGubun);
  const [gubun, setGubun] = useState({ type: 'tuGubun', state: '07', cdName: '화재경보' });
  const [searchOption, setSearchOption] = useState({ tuName: '' });
  const [searchOptionUseYn, setSearchOptionUseYn] = useState(INIT_USE_YN_A);
  const [tableState, setTableState] = useState<ItuList[]>([]);
  const [initTableState, setInitTableState] = useState<ItuList[]>([]);
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const scrollContainerRef = useRef<HTMLInputElement>(null);
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const inputRefs = useRef<HTMLInputElement[] | null>(Array(tableState.length).fill(null));
  const tableStateRef = useRef(tableState);
  const { data: useYnComCdListWithAll } = useFetchCommonCodeList(COMCD_USE_YN, true); // 사용여부 공통코드 목록 (전체포함)
  const { data: useYnComCdList } = useFetchCommonCodeList(COMCD_USE_YN, false); // 사용유무 공통코드 목록

  useEffect(() => {
    if (prop.tuGubun) {
      setTuGubun(prop.tuGubun);
      setGubun(gubunList.find((el) => el.state === prop.tuGubun) || gubunList[0]);
      logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '알림톡 담당자 관리',
        action: '조회',
        etc: ``,
      });
    }
  }, [prop.tuGubun]);

  useEffect(() => {
    //! AI 경고 목록인 경우에만 실행
    if (isAiWarning(tuGubun)) {
      setTuGubun(gubun.state);
    }
  }, [gubun]);

  const isAiWarning = (key: string) => {
    return key === '07' || key === '08' || key === '09';
  };

  useEffect(() => {
    tableStateRef.current = tableState;
  }, [tableState]);

  // 긴급 상황목록 조회 useQuery
  const emergencyMsgListQuery = useQuery(['emergencyMsgListQuery', userInfo.hCd, userInfo.sCd, tuGubun], () => fetchEmergencyMsgList(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && !!tuGubun,
  });

  // 긴급 상황목록 조회 데이터패치 함수
  const fetchEmergencyMsgList = async () => {
    try {
      const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, tuGubun };
      const res = await apiGet({ path: '/msg', req });
      const { tuList } = res.data.data || [];
      setTableState(tuList.map((el: ItuList) => ({ ...el, flag: FLAG_NONE })));
      setInitTableState(tuList.map((el: ItuList) => ({ ...el, flag: FLAG_NONE })));
      return tuList;
    } catch (error) {
      console.error(error);
      throw new Error('error /msg');
    }
  };

  useEffect(() => {
    setSearchOptionUseYn(INIT_USE_YN_A);
  }, [i18n.language]);

  useEffect(() => {
    if (tableState.length > 0) scrollToNodeBottom(scrollContainerRef);
  }, [tableState.length]);

  useEffect(() => {
    setSearchOption({ tuName: '' });
  }, [tuGubun]);

  useEffect(() => {
    applyFilter(initTableState);
  }, [searchOption.tuName, searchOptionUseYn[COMCD_USE_YN]]);

  const applyFilter = (array: ItuList[]) => {
    // 검색옵션 변경됐을 때 필터링 처리
    // 필터링된 어레이 리턴, 대소문자구분X
    const titleFilteredArray = array.filter((el: any) => el.tuName?.toLowerCase()?.includes(searchOption.tuName?.toLowerCase()));
    const result = ynFilter(titleFilteredArray, 'useYn', searchOptionUseYn[COMCD_USE_YN]);
    if (result.length > 0) setTableState(result);
    else setTableState([]);
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    setSearchOptionUseYn(INIT_USE_YN_A);
    setSearchOption({ tuName: '' });
  };

  const onClickAddTableRow = () => {
    const newObj = { tuGubun, tuCd: '', tuName: '', tuTel: '', useYn: 'Y', bigo: '', editor: '' };
    setTableState((prev: ItuList[]) => [...prev, newObj]);
  };

  const onClickSave = () => {
    setIsSaveClicked(true);
    const required: (keyof ItuList)[] = ['tuName', 'tuTel'];
    const emptyCheck = tableState.filter((el: ItuList) => {
      const check = required.find((key: keyof ItuList) => el[key] === '' && el.flag === FLAG_CREATE_OR_UPDATE);
      return check !== undefined;
    });
    const tuTelFocusIndex = tableState.findIndex((el) => el.tuTel.length < 10);

    if (emptyCheck.length > 0) {
      toast.warning(t('필수입력값을 모두 입력하세요'));
    } else if (tuTelFocusIndex !== -1) {
      // 휴대폰번호 9자리 이하일경우 포커스
      // Delay the focus to allow for state update
      setTimeout(() => {
        inputRefs.current && inputRefs.current[tuTelFocusIndex]?.focus();
        toast.warning(t('휴대폰 번호를 확인하세요.'));
      }, 0);
    } else {
      const updateArray = tableState.filter((el: ItuList) => el.flag === FLAG_CREATE_OR_UPDATE);
      if (updateArray.length > 0) postAPI(updateArray);
    }
  };

  const postAPI = async (array: ItuList[]) => {
    const newArray = array.map((el: ItuList) => ({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      tuGubun,
      tuCd: el.tuCd,
      tuName: el.tuName,
      tuTel: el.tuTel,
      bigo: el.bigo,
      useYn: el.useYn,
      editor: userInfo.userId,
      ...(el.tuCd === '' && { writer: userInfo.userId }),
    }));
    const req = { tuSaveReqDto: trimArray(newArray) };
    const res = await apiPost({ path: '/msg', req });
    const { data, statusCode, message } = res.data || {};
    if (statusCode === 200) {
      const { tuList } = data || [];
      applyFilter(tuList.map((el: ItuList) => ({ ...el, flag: FLAG_NONE })));
      setInitTableState(tuList.map((el: ItuList) => ({ ...el, flag: FLAG_NONE })));
      setIsSaveClicked(false);
      toast.success(t(message));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '알림톡 담당자 관리',
        action: '담당자 등록',
        etc: ``,
      });
    }
  };

  const deleteAPI = async (el: ItuList) => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, tuGubun, tuCd: el.tuCd, editor: userInfo.userId };
    const res = await apiDelete({ path: '/msg', req });
    const { data, statusCode, message } = res.data;
    if (statusCode === 200) {
      setIsSaveClicked(false);
      toast.success(t(message));
      applyFilter(data.tuList.map((resEl: ItuList) => ({ ...resEl, flag: FLAG_NONE })));
      setInitTableState(data.tuList.map((resEl: ItuList) => ({ ...resEl, flag: FLAG_NONE })));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '알림톡 담당자 관리',
        action: '담당자 삭제',
        etc: `담당자 명 : ${el.tuName}`,
      });
    } else toast.error(t(ERROR));
  };

  const onClickDelete = (el: ItuList, i: number) => {
    if (el.tuCd) {
      // 기존 데이터 삭제시
      const data = { ...el, editor: userInfo.userId };
      setOpenModal((prev) => ({ ...prev, type: 'delete', status: true, state: tableState, setState: setTableState, api: () => deleteAPI(data), el: data, index: i }));
    } else {
      // 제거 (새로 추가한 데이터 제거시)
      const updatedArray = [...tableState];
      updatedArray.splice(i, 1);
      setTableState(updatedArray);
    }
  };

  if (!emergencyMsgListQuery || !emergencyMsgListQuery.data)
    return (
      <LoadingModalBackground>
        <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
      </LoadingModalBackground>
    );

  return (
    <div className='content-container oneColumn'>
      <Root loading={emergencyMsgListQuery.isLoading}>
        <SearchOptions align='left'>
          {size.innerSize.W > 1024 ? (
            <div className='inputsWrapper'>
              <div className='flex-basic textBtnGroup'>
                <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
              </div>
              <div className='inputForm-row'>
                <div className='inputForm-col withLabelComCf'>
                  <label htmlFor='useYn'>{t('사용유무')}</label>
                  <SelectBox
                    options={useYnComCdListWithAll}
                    defaultOption={searchOptionUseYn.cdName}
                    state={searchOptionUseYn}
                    setState={setSearchOptionUseYn}
                    stateKey={COMCD_USE_YN}
                    initiateKey={searchOptionUseYn[COMCD_USE_YN]}
                    filterbar='filter-1-left'
                  />
                </div>
              </div>
              {isAiWarning(tuGubun) && (
                <div className='inputForm-row'>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='useYn'>{t('구분')}</label>
                    <SelectBox options={gubunList} defaultOption={gubun.cdName} state={gubun} setState={setGubun} stateKey='state' initiateKey={gubun.state} />
                  </div>
                </div>
              )}
              <div className='inputForm-row'>
                <div className='inputForm-col'>
                  <Input label='' placeholder={t('담당자명')} type='text' id='tuName' name='tuName' state={searchOption} setState={setSearchOption} />
                </div>
              </div>
            </div>
          ) : (
            <div className='filter-wrapper'>
              <div className='inputsWrapper'>
                {isAiWarning(tuGubun) && (
                  <div className='inputForm-row'>
                    <div className='inputForm-col withLabelComCf'>
                      <label htmlFor='useYn'>{t('구분')}</label>
                      <SelectBox options={gubunList} defaultOption={gubun.cdName} state={gubun} setState={setGubun} stateKey='state' initiateKey={gubun.state} />
                    </div>
                  </div>
                )}
                <div className='inputForm-row' style={{ width: '100%' }}>
                  <div className='inputForm-col'>
                    <Input label='' placeholder={t('담당자명')} type='search' id='tuName' name='tuName' state={searchOption} setState={setSearchOption} ico='search' />
                  </div>
                </div>
              </div>
            </div>
          )}
        </SearchOptions>
        <InputTable className='margin-left-05' ref={scrollContainerRef}>
          <div className='thead'>
            <div className='tr'>
              <div className='trCol2p5 flex-center tableStickyNo'>No</div>
              <div className='trCol4 flex-center'>{t('코드')}</div>
              <div className='trCol12 flex-center required'>{t('담당자명')}</div>
              <div className='trCol12 flex-center required'>{t('휴대폰번호')}</div>
              <div className='trCol8 flex-center'>{t('사용유무')}</div>
              <div className='trCol12 flex-center content-overflow'>{t('비고')}</div>
              <div className='trCol10 flex-center'>{t('등록일자')}</div>
              <div className='trCol6 flex-center'>{t('등록자')}</div>
              <div className='trCol10 flex-center'>{t('수정일자')}</div>
              <div className='trCol6 flex-center'>{t('수정자')}</div>
              {auth.deleteAuth && <div className='trCol4 flex-center'> </div>}
            </div>
          </div>
          <div className='table img'>
            <div className='tbody'>
              {tableState.length > 0 ? (
                tableState.map((el: ItuList, index: number) => (
                  <div className='tr' key={`${index}_${el.tuCd}`}>
                    <div className='trCol2p5 flex-center tableStickyNo'>{index + 1}</div>
                    <div className='trCol4 flex-center'>{el.tuCd}</div>
                    <div className='trCol12 flex-center'>
                      <Input
                        type='text'
                        id='tuName'
                        name='tuName'
                        state={tableState}
                        setState={setTableState}
                        stateType={{ type: 'array', index }}
                        getBorderStyle={isSaveClicked ? applyBorderStyle(el.tuName, 'red', 'name') : undefined}
                        useFlag
                        maxLength={8}
                      />
                    </div>
                    <div className='trCol12 flex-center'>
                      <Input
                        placeholder={`' - ' 제외하고 입력`}
                        type='text'
                        id='tuTel'
                        name='tuTel'
                        state={tableState}
                        setState={setTableState}
                        stateType={{ type: 'array', index }}
                        getBorderStyle={isSaveClicked ? applyBorderStyle(el.tuTel, 'red', 'tel') : undefined}
                        useFlag
                        valueType='stringNumber'
                        maxLength={11}
                        inputRef={(input: any) => {
                          const refs = inputRefs?.current;
                          if (refs) {
                            refs[index] = input;
                          }
                        }}
                      />
                    </div>
                    <div className='trCol8 flex-center'>
                      <SelectBoxs
                        options={useYnComCdList}
                        defaultOption={el.useYn === 'Y' ? t('사용') : t('미사용')}
                        state={tableState}
                        setState={setTableState}
                        rawData={initTableState}
                        setRawData={setInitTableState}
                        stateKey='useYn'
                        codeKey='cdName'
                        index={index}
                        object={el}
                        primaryKey='useYn'
                        initiateKey={tableState[index].useYn}
                      />
                    </div>
                    <div className='trCol12 flex-center content-overflow'>
                      <Input type='text' id='bigo' name='bigo' state={tableState} setState={setTableState} stateType={{ type: 'array', index }} useFlag maxLength={200} />
                    </div>
                    <div className='trCol10 flex-center'>{el.wDate}</div>
                    <div className='trCol6 flex-center'>{el.writer}</div>
                    <div className='trCol10 flex-center'>{el.eDate}</div>
                    <div className='trCol6 flex-center'>{el.editor}</div>
                    {auth.deleteAuth && (
                      <div className='trCol4 flex-center'>
                        <BtnRed onClick={() => onClickDelete(el, index)}>{el.tuCd ? t('삭제') : t('제거')}</BtnRed>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <IssueGuide />
              )}
            </div>
          </div>
        </InputTable>
        <ButtonsWrapper>
          {auth.createAuth && <BtnBlue onClick={onClickAddTableRow}>{t('신규항목 추가')}</BtnBlue>}
          {(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}
        </ButtonsWrapper>
      </Root>
      {emergencyMsgListQuery.isLoading ? (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      ) : undefined}
      <Portal openModal={openModal?.status}>{openModal && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}</Portal>
    </div>
  );
};

export default EmergencyList;
